import { Button, Form, Input, message } from 'antd';
import { FC, ReactNode, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LayoutSite } from '../components/layout';
import { Loader } from '../components/loader';
import { import_file, preview_import_file } from '../services/api.service';
import './importfr.scss';
import { APIAnswer, APIAnswerData, MLCompetitor, MLRace } from '../types';
import { ArrowLeftOutlined, DownOutlined, FileAddOutlined, FileTextOutlined, UpOutlined } from '@ant-design/icons';

/* -------------------------------------------------------------------------------- *\
|                               ImportFRPage
\* -------------------------------------------------------------------------------- */
export const ImportFRPage: FC<{ children?: ReactNode }> = ({ children }) =>
{
    const [loading, setLoading] = useState(true);
    const [selectedtab, setSelectedTab] = useState<string>("races");
    const [previewraces, setPreviewRaces] = useState<MLRace[]>([]);
    const navigate = useNavigate();
    const [inputfilekey, setinputfilekey] = useState<string>('key_0');
    const [openRaces, setOpenRaces] = useState<Set<number>>(new Set());

    /* -------------------------------------------------------------------------------- *\
    |                               toggleRace / toggleAllRaces
    \* -------------------------------------------------------------------------------- */
    function toggleRace(index: number)
    {
        const next = new Set(openRaces);
        if (next.has(index)) next.delete(index);
        else next.add(index);
        setOpenRaces(next);
    }

    function toggleAllRaces()
    {
        if (openRaces.size > 0) setOpenRaces(new Set());
        else setOpenRaces(new Set(previewraces.map((_, i) => i)));
    }

    /* -------------------------------------------------------------------------------- *\
    |                               effects
    \* -------------------------------------------------------------------------------- */
    useEffect(() => {
        setLoading(true);
        setSelectedTab("races");
        setLoading(false);
        // eslint-disable-next-line
    }, [])

    const [file, setFile] = useState<File | null>(null);
    const [preview, setPreview] = useState<string>('');

    /* -------------------------------------------------------------------------------- *\
    |                               handleUpload
    \* -------------------------------------------------------------------------------- */
    function handleUpload(event)
    {
        const e: HTMLInputElement = event.target;
        if (e.files && e.files[0])
        {
            const f: File = e.files[0];
            setFile(f);

            var reader = new FileReader();
            reader.addEventListener('load', (e) =>
            {
                if (e.loaded === e.total)
                {
                    try
                    {
                        const result = e.target?.result as string;
                        var c = JSON.parse(result);
                        setPreview(JSON.stringify(c, null, 2));
                        setPreviewRaces([]);
                        setOpenRaces(new Set());
                        preview_import_file(result)
                        .then((data: APIAnswerData) =>
                        {
                            if (data.status)
                            {
                                const races = data.data ?? [];
                                setPreviewRaces(races);
                                setOpenRaces(new Set(races.map((_, i) => i)));
                            }
                            else message.error(data.message || "Preview failed");
                        })
                        .catch((error) => { message.error(error?.message || "Preview failed"); });
                    }
                    catch(error)
                    {
                        setPreview('Error reading file : ' + error);
                    }
                    setinputfilekey(inputfilekey.split('_')[0] + '_' + (parseInt(inputfilekey.split('_')[1]) + 1));
                }
            });
            reader.readAsText(f as Blob);
        }
    }
    /* -------------------------------------------------------------------------------- *\
    |                               onFinishUpload
    \* -------------------------------------------------------------------------------- */
    function onFinishUpload()
    {
        var reader = new FileReader();
        reader.addEventListener('load', (e) =>
        {
            if (e.loaded === e.total)
            {
                import_file(e.target?.result as string)
                .then((answer: APIAnswer) =>
                {
                    if (answer.status)
                    {
                        message.info("import succeded");
                        navigate("/");
                    }
                    else
                    {
                        message.error("An error occured while importing: " + answer.message);
                    }
                })
                .catch((error) => { message.info(error); })
            }
        });
        reader.readAsText(file as Blob);
    }

    return (
        <LayoutSite>
            <div className="importfr">
                {loading ? <Loader /> : <></>}
                <div className="importfr__header">
                    <div>
                        <button className="importtracks__header__back" onClick={() => navigate("/tracks")}>
                            <ArrowLeftOutlined />
                            calendar
                        </button>
                    </div>
                </div>
                <div className="importfr__body">
                    <div className="importfr__body__title">Import race from JSON file</div>
                    <div className="importfr__body__subtitle">This this the <b>old racing file format</b>.</div>
                    <div className="importfr__body__subtitle">If you want to import a race whith the new file format, use the 'New Race' menu.</div>
                    <Form name="importfr__body__form" className="importfr__body__form" onFinish={() => onFinishUpload()}>
                        <Form.Item label='File'>
                            <div className="importfr-form-file-wrapper">
                                <div className="importfr-form-file-container">
                                    <FileAddOutlined style={{ fontSize: "24px" }} />
                                    <h4 className="importfr-form-file-title">Add your JSON file to see the preview or import</h4>
                                </div>
                                <Input className="importfr-form-file" type="file" hidden={true} onChange={handleUpload} key={inputfilekey} accept=".json" />
                            </div>
                        </Form.Item>
                        {
                            file &&
                            <p className="importfr-form-file-name">
                                <FileTextOutlined style={{ fontSize: "16px" }} />
                                {file?.name}
                            </p>
                        }
                        <Form.Item className="importfr__body__form__toolbar">
                            <Button type="primary" htmlType="submit" disabled={!file}>Import</Button>
                        </Form.Item>
                    </Form>
                    {
                        preview &&
                        <>
                            <div className='importfr__body__preview__buttons'>
                                <Button size='small' onClick={() => setSelectedTab("races")}>Races</Button>
                                <Button size='small' onClick={() => setSelectedTab("text")}>Text</Button>
                            </div>

                            {
                                selectedtab === 'races' &&
                                <>
                                    <Button className="importfr-preview-close" size='small' type="primary" onClick={toggleAllRaces}>
                                        {openRaces.size > 0 ? "Close All" : "Open All"}
                                    </Button>
                                    <div className='importfr-preview'>
                                        <div className='importfr-preview-races'>
                                            {
                                                previewraces?.map((race: MLRace, index: number) =>
                                                {
                                                    const date = new Date(race.date);

                                                    return (
                                                        <div className="importfr-preview-race" key={index} onClick={() => toggleRace(index)}>
                                                            <div className="importfr-preview-race-container">
                                                                <div className="importfr-preview-race-line">
                                                                    <div>C{race.number}</div>
                                                                    <div>{!isNaN(date.getTime()) ? date.toLocaleTimeString().substring(0, 5) : ''}</div>
                                                                    <div> - {race.distance}m</div>
                                                                    <div> - <span className={race.trackcode === "XXX" ? "importfr-trackcode-invalid" : undefined}>{race.trackcode}</span></div>
                                                                    <div>{" - count=" + race.competitors.length}</div>
                                                                    <div> {race.name}</div>
                                                                </div>
                                                                <div style={{ cursor: 'pointer' }}>
                                                                    {openRaces.has(index) ? <UpOutlined style={{ fontSize: "20px" }} /> : <DownOutlined style={{ fontSize: "20px" }} />}
                                                                </div>
                                                            </div>
                                                            {
                                                                openRaces.has(index) && race.competitors.map((horse: MLCompetitor, i: number) =>
                                                                {
                                                                    return (
                                                                        <div key={i} className="importfr-preview-race-horse">
                                                                            <div>{horse.number}</div>
                                                                            <div>{horse.name}</div>
                                                                        </div>
                                                                    )
                                                                })
                                                            }
                                                        </div>
                                                    )
                                                })
                                            }
                                        </div>
                                    </div>
                                </>
                            }
                            {
                                selectedtab === 'text' &&
                                <div className='importfr__body__preview'>
                                    <div className='importfr__body__preview__races'>{preview ? preview : ''}</div>
                                </div>
                            }
                        </>
                    }
                </div>
            </div>
        </LayoutSite>
    );
}
